/* =============================================================================
   Forevermore Magnets — product-uploader.js
   Pack selection, multi-photo upload, 2:3 crop with zoom/reposition,
   per-photo quantity assignment, and Add to Cart gating.

   PRODUCTION UPLOAD INTEGRATION -------------------------------------------
   Browsers cannot persist customer files into a Shopify order by themselves.
   This script intercepts the Add-to-Cart submit, uploads every assigned photo
   to an external host, writes the returned download URLs into the line-item
   properties, and then submits the cart form.

   To activate upload hosting, render this snippet somewhere in the product
   template (e.g. at the bottom of main-product.liquid) before this script
   runs:

       <script>
         window.ForevermoreMagnets = window.ForevermoreMagnets || {};
         // Uploadery endpoint — copy from the app's "Integration" tab:
         window.ForevermoreMagnets.uploadUrl = "https://app.uploadery.com/upload/<YOUR_STORE_HANDLE>";
         // Optional: Bearer token if required by the app
         // window.ForevermoreMagnets.uploadToken = "{{ shop.metafields.uploadery.token }}";
       </script>

   When `uploadUrl` is not set (development / staging), the script falls back
   to using the file name in the property so the rest of the UX still works.
   See README → "Photo uploads in production" for the full setup steps.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.getElementById('ProductRoot');
  if (!root) return;

  // ---- Config -------------------------------------------------------------
  var MIN_WIDTH = 600;   // px — below this we warn about print quality (2in @300dpi)
  var MIN_HEIGHT = 900;  // px — 3in @ 300dpi
  var ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

  // Upload endpoint injected by the Uploadery (or equivalent) app snippet.
  // Null = development fallback (file name stored instead of URL).
  var cfg = window.ForevermoreMagnets || {};
  var UPLOAD_URL = cfg.uploadUrl || null;
  var UPLOAD_TOKEN = cfg.uploadToken || null;

  // Map variant titles to magnet counts. Works for "Single", "5-Pack",
  // "9-Pack", "50-Pack" and reasonable variations ("1 Magnet", "9 Magnets").
  function packSizeFromTitle(title) {
    if (/single/i.test(title)) return 1;
    var m = title.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 1;
  }

  // ---- State ----------------------------------------------------------------
  var photos = [];      // { id, file, objectUrl, width, height, qty, crop:{x,y,zoom}, warnings:[], hostedUrl:null, uploadProgress:null, uploadError:null }
  var packSize = 1;
  var nextId = 1;
  var isUploading = false;
  var uploadTotal = 0;    // total photos being uploaded in the current batch
  var uploadComplete = 0; // photos successfully uploaded so far in the current batch

  // ---- Elements ---------------------------------------------------------------
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var packPicker = $('[data-pack-picker]');
  var drop = $('[data-uploader-drop]');
  var fileInput = $('[data-file-input]');
  var photoList = $('[data-photo-list]');
  var counter = $('[data-assign-counter]');
  var counterText = $('[data-assign-text]');
  var counterFill = $('[data-assign-fill]');
  var assignHint = $('[data-assign-hint]');
  var useForAllBtn = $('[data-use-for-all]');
  var atcBtn = $('[data-add-to-cart]');
  var atcHelp = $('[data-atc-help]');
  var stickyBar = $('[data-sticky-atc]');
  var stickyBtn = $('[data-sticky-atc-btn]');
  var propsContainer = $('[data-photo-properties]');
  var form = document.getElementById('ProductForm');
  var variantInput = $('[data-variant-id-input]');

  // ---- Screen reader live regions ------------------------------------------
  // Polite region announces overall upload state ("Uploading 1 of 3 photos…");
  // assertive region announces upload errors immediately.
  var liveStatus = createLiveRegion('polite');
  var liveAlert = createLiveRegion('assertive');
  var lastStatusMessage = '';

  function createLiveRegion(politeness) {
    var el = document.createElement('div');
    el.className = 'visually-hidden';
    el.setAttribute('aria-live', politeness);
    el.setAttribute('role', politeness === 'assertive' ? 'alert' : 'status');
    el.setAttribute('aria-atomic', 'true');
    root.appendChild(el);
    return el;
  }

  // Announce overall status politely; skips duplicate messages so screen
  // readers are not spammed on every render/progress tick.
  function announceStatus(message) {
    if (message === lastStatusMessage) return;
    lastStatusMessage = message;
    liveStatus.textContent = message;
  }

  // Announce an error immediately (assertive).
  function announceError(message) {
    // Clear first so repeating the same error text is re-announced.
    liveAlert.textContent = '';
    liveAlert.textContent = message;
  }

  var cancelAllBtn = null; // created lazily inside the counter area

  var readyLabel = 'Add to Cart';
  var uploadingLabel = 'Uploading photos…';
  var lockedLabel = $('[data-atc-label]') ? $('[data-atc-label]').textContent : 'Assign all magnets to continue';

  // ---- Pack picker ------------------------------------------------------------
  function currentPackRadio() {
    return packPicker ? packPicker.querySelector('input[name="pack"]:checked') : null;
  }

  function moneyFormat(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function initPackPicker() {
    if (!packPicker) return;
    var radios = packPicker.querySelectorAll('input[name="pack"]');
    radios.forEach(function (radio) {
      var size = packSizeFromTitle(radio.dataset.variantTitle || '');
      var price = parseInt(radio.dataset.price || '0', 10);
      var per = radio.closest('.pack-option').querySelector('[data-per-magnet]');
      if (per && size > 1 && price > 0) {
        per.textContent = '≈ ' + moneyFormat(Math.round(price / size)) + ' each';
      }
      radio.addEventListener('change', onPackChange);
    });
    onPackChange();
  }

  function onPackChange() {
    var radio = currentPackRadio();
    if (!radio) return;
    packSize = packSizeFromTitle(radio.dataset.variantTitle || '');
    rebalanceForPackSize();
    if (variantInput) variantInput.value = radio.value;
    document.querySelectorAll('[data-price]').forEach(function (el) {
      el.textContent = moneyFormat(parseInt(radio.dataset.price || '0', 10));
    });
    render();
  }

  // ---- Upload -------------------------------------------------------------------
  function initUploader() {
    if (!drop || !fileInput) return;
    drop.addEventListener('click', function () { fileInput.click(); });
    drop.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
    });
    ['dragover', 'dragenter'].forEach(function (evt) {
      drop.addEventListener(evt, function (e) { e.preventDefault(); drop.classList.add('is-dragover'); });
    });
    ['dragleave', 'drop'].forEach(function (evt) {
      drop.addEventListener(evt, function (e) { e.preventDefault(); drop.classList.remove('is-dragover'); });
    });
    drop.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files);
    });
    fileInput.addEventListener('change', function () {
      addFiles(fileInput.files);
      fileInput.value = '';
    });
  }

  // Single validation pipeline used for both new uploads and replacements:
  // format check, dimension probe, low-res warning, HEIC preview fallback,
  // and unreadable-file warning all happen here.
  function attachFileToPhoto(photo, file) {
    if (photo.objectUrl) URL.revokeObjectURL(photo.objectUrl);
    photo.file = file;
    photo.objectUrl = URL.createObjectURL(file);
    photo.width = 0;
    photo.height = 0;
    photo.crop = { x: 0, y: 0, zoom: 1 };
    photo.warnings = [];
    photo.hostedUrl = null;       // cleared when file changes; filled on submit
    photo.uploadProgress = null;  // null = idle; 0–100 = uploading; cleared on success
    photo.uploadError = null;     // null = no error; string = failed message

    var isHeic = /\.heic$|\.heif$/i.test(file.name);
    if (ACCEPTED.indexOf(file.type) === -1 && !isHeic) {
      photo.warnings.push('Unsupported format — please use JPG, PNG, or HEIC.');
    }

    var img = new Image();
    img.onload = function () {
      photo.width = img.naturalWidth;
      photo.height = img.naturalHeight;
      if (photo.width < MIN_WIDTH || photo.height < MIN_HEIGHT) {
        photo.warnings.push('Low resolution — this photo may print blurry at 2 × 3 in.');
      }
      render();
    };
    img.onerror = function () {
      if (isHeic) {
        // HEIC previews are not supported by all browsers; still accepted.
        photo.warnings.push('Preview unavailable in this browser (HEIC). The photo itself is fine.');
      } else {
        photo.warnings.push('Could not read this image.');
      }
      render();
    };
    img.src = photo.objectUrl;
  }

  function addFiles(fileList) {
    Array.prototype.forEach.call(fileList, function (file) {
      var photo = {
        id: nextId++,
        file: null,
        objectUrl: null,
        width: 0,
        height: 0,
        qty: 0,
        crop: { x: 0, y: 0, zoom: 1 },
        warnings: [],
        hostedUrl: null
      };
      photos.push(photo);
      attachFileToPhoto(photo, file);
    });

    autoAssign();
    render();
  }

  // Auto-assign sensibly: first photo of a fresh upload takes remaining slots
  // only when it is the ONLY photo (fast single/all-same flow).
  function autoAssign() {
    if (photos.length === 1 && totalAssigned() === 0) {
      photos[0].qty = packSize;
    } else if (photos.length > 1) {
      // give each unassigned photo 1 while capacity remains
      photos.forEach(function (p) {
        if (p.qty === 0 && totalAssigned() < packSize) p.qty = 1;
      });
    }
  }

  // When the customer switches packs after uploading photos, keep their
  // assignments sane: shrink the overflow when the pack gets smaller, and
  // top up unassigned photos when it gets bigger. Without this, photos
  // uploaded while a smaller pack was selected stay capped (e.g. stuck at
  // one magnet per photo after starting on a Single).
  function rebalanceForPackSize() {
    if (photos.length === 0) return;
    // Clamp each photo to the new pack size.
    photos.forEach(function (p) { p.qty = Math.min(p.qty, packSize); });
    // If the total still exceeds the pack, trim from the last photos first.
    var over = totalAssigned() - packSize;
    for (var i = photos.length - 1; i >= 0 && over > 0; i--) {
      var take = Math.min(photos[i].qty, over);
      photos[i].qty -= take;
      over -= take;
    }
    // Top up: give unassigned photos one magnet each while capacity remains.
    autoAssign();
  }

  function removePhoto(id) {
    var idx = photos.findIndex(function (p) { return p.id === id; });
    if (idx !== -1) {
      URL.revokeObjectURL(photos[idx].objectUrl);
      photos.splice(idx, 1);
      render();
    }
  }

  function replacePhoto(id) {
    var temp = document.createElement('input');
    temp.type = 'file';
    temp.accept = ACCEPTED.join(',');
    temp.addEventListener('change', function () {
      var file = temp.files && temp.files[0];
      if (!file) return;
      var photo = photos.find(function (p) { return p.id === id; });
      if (!photo) return;
      // Re-run the exact same validation pipeline as a fresh upload.
      attachFileToPhoto(photo, file);
      render();
    });
    temp.click();
  }

  // ---- Assignment --------------------------------------------------------------
  function totalAssigned() {
    return photos.reduce(function (sum, p) { return sum + p.qty; }, 0);
  }

  function setQty(id, qty) {
    var photo = photos.find(function (p) { return p.id === id; });
    if (!photo) return;
    photo.qty = Math.max(0, Math.min(packSize, qty | 0));
    render();
  }

  function useForAll() {
    if (photos.length === 0) return;
    photos.forEach(function (p, i) { p.qty = i === 0 ? packSize : 0; });
    render();
  }

  // ---- Crop modal -----------------------------------------------------------------
  var modal = $('[data-crop-modal]');
  var stage = $('[data-crop-stage]');
  var cropImg = $('[data-crop-image]');
  var zoomInput = $('[data-crop-zoom]');
  var cropPhotoId = null;
  var draft = { x: 0, y: 0, zoom: 1 };
  var dragState = null;

  function openCrop(id) {
    var photo = photos.find(function (p) { return p.id === id; });
    if (!photo || !modal) return;
    cropPhotoId = id;
    draft = { x: photo.crop.x, y: photo.crop.y, zoom: photo.crop.zoom };
    cropImg.src = photo.objectUrl;
    cropImg.alt = 'Crop preview for ' + photo.file.name;
    zoomInput.value = draft.zoom;
    modal.hidden = false;
    modal.classList.add('is-open');
    cropImg.onload = applyCropTransform;
    applyCropTransform();
    $('[data-crop-save]').focus();
  }

  function closeCrop() {
    modal.classList.remove('is-open');
    modal.hidden = true;
    cropPhotoId = null;
  }

  function applyCropTransform() {
    if (!cropImg.naturalWidth || !stage) return;
    var stageRect = stage.getBoundingClientRect();
    // "cover" base scale for a 2:3 stage
    var scale = Math.max(stageRect.width / cropImg.naturalWidth, stageRect.height / cropImg.naturalHeight) * draft.zoom;
    var w = cropImg.naturalWidth * scale;
    var h = cropImg.naturalHeight * scale;
    // clamp offsets so the stage stays covered
    var maxX = Math.max(0, (w - stageRect.width) / 2);
    var maxY = Math.max(0, (h - stageRect.height) / 2);
    draft.x = Math.max(-maxX, Math.min(maxX, draft.x));
    draft.y = Math.max(-maxY, Math.min(maxY, draft.y));
    cropImg.style.width = w + 'px';
    cropImg.style.height = h + 'px';
    cropImg.style.transform = 'translate(calc(-50% + ' + draft.x + 'px), calc(-50% + ' + draft.y + 'px))';
  }

  function initCrop() {
    if (!modal) return;
    zoomInput.addEventListener('input', function () {
      draft.zoom = parseFloat(zoomInput.value);
      applyCropTransform();
    });
    stage.addEventListener('pointerdown', function (e) {
      dragState = { startX: e.clientX, startY: e.clientY, baseX: draft.x, baseY: draft.y };
      stage.setPointerCapture(e.pointerId);
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragState) return;
      draft.x = dragState.baseX + (e.clientX - dragState.startX);
      draft.y = dragState.baseY + (e.clientY - dragState.startY);
      applyCropTransform();
    });
    ['pointerup', 'pointercancel'].forEach(function (evt) {
      stage.addEventListener(evt, function () { dragState = null; });
    });
    $('[data-crop-cancel]').addEventListener('click', closeCrop);
    modal.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeCrop(); });
    $('[data-crop-save]').addEventListener('click', function () {
      var photo = photos.find(function (p) { return p.id === cropPhotoId; });
      if (photo) photo.crop = { x: Math.round(draft.x), y: Math.round(draft.y), zoom: Math.round(draft.zoom * 100) / 100 };
      closeCrop();
      render();
    });
  }

  // ---- Storage upload ----------------------------------------------------------
  // Uploads a single file to the configured endpoint and returns a Promise that
  // resolves to the hosted download URL string.
  //
  // The endpoint is set by the Uploadery (or equivalent) app's liquid snippet:
  //   window.ForevermoreMagnets.uploadUrl = "https://app.uploadery.com/upload/<handle>";
  //
  // Uploadery expects a multipart/form-data POST with the field name "file" and
  // responds with JSON: { "url": "https://cdn.uploadery.com/…/photo.jpg" }
  //
  // If no endpoint is configured (local dev / staging), the function resolves
  // immediately with null so the order property stores the file name instead.
  //
  // onProgress(pct) is called with integer 0–100 as the upload progresses.
  function uploadToStorage(photo, onProgress) {
    if (!UPLOAD_URL) {
      // Development fallback — no app installed yet.
      return Promise.resolve(null);
    }

    var data = new FormData();
    data.append('file', photo.file, photo.file.name);
    // Uploadery (and most upload apps) accept optional metadata fields:
    data.append('order_reference', 'forevermore_magnets');
    data.append('photo_id', String(photo.id));

    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      // Keep a handle so the customer can cancel a stuck upload.
      photo.uploadXhr = xhr;
      function clearHandle() { if (photo.uploadXhr === xhr) photo.uploadXhr = null; }

      // Report upload progress so the UI can show a per-photo progress bar.
      if (onProgress) {
        xhr.upload.addEventListener('progress', function (e) {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        });
      }

      xhr.addEventListener('load', function () {
        clearHandle();
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            var json = JSON.parse(xhr.responseText);
            // Uploadery returns { url: "https://…" }; adjust the property name if
            // your chosen app uses a different key (e.g. "file_url", "download_url").
            var url = json.url || json.file_url || json.download_url || null;
            if (!url) { reject(new Error('Upload app returned no URL in response.')); return; }
            resolve(url);
          } catch (e) {
            reject(new Error('Invalid JSON from upload server.'));
          }
        } else {
          reject(new Error('Upload failed (' + xhr.status + ')'));
        }
      });

      xhr.addEventListener('error', function () { clearHandle(); reject(new Error('Network error during upload.')); });
      xhr.addEventListener('abort', function () { clearHandle(); reject(new Error('Upload cancelled.')); });

      xhr.open('POST', UPLOAD_URL);
      if (UPLOAD_TOKEN) xhr.setRequestHeader('Authorization', 'Bearer ' + UPLOAD_TOKEN);
      xhr.send(data);
    });
  }

  // ---- Cancel uploads ------------------------------------------------------------
  // Aborts the in-flight XHR for one photo. The abort listener rejects the
  // upload promise, which routes through the existing failure path (red
  // border + "Retry upload" button) and settles the batch bookkeeping.
  function cancelUpload(id) {
    var photo = photos.find(function (p) { return p.id === id; });
    if (photo && photo.uploadXhr) photo.uploadXhr.abort();
  }

  function cancelAllUploads() {
    photos.forEach(function (p) { if (p.uploadXhr) p.uploadXhr.abort(); });
  }

  // Returns the best available reference for the "Photo N" line-item property:
  // hosted URL if the file has already been uploaded, file name otherwise.
  // syncProperties() uses this for the live preview; the authoritative URL is
  // written during the async submit flow below.
  function photoReference(photo) {
    return photo.hostedUrl || photo.file.name;
  }

  // ---- Line-item properties --------------------------------------------------------
  function syncProperties() {
    if (!propsContainer) return;
    propsContainer.innerHTML = '';
    var i = 1;
    photos.forEach(function (photo) {
      if (photo.qty === 0) return;
      addProp('Photo ' + i, photoReference(photo));
      addProp('Photo ' + i + ' quantity', String(photo.qty));
      addProp('Photo ' + i + ' crop', 'x:' + photo.crop.x + ' y:' + photo.crop.y + ' zoom:' + photo.crop.zoom);
      if (photo.warnings.length) addProp('Photo ' + i + ' warnings', photo.warnings.join(' | '));
      i++;
    });
    addProp('_Photos total', String(totalAssigned()) + ' of ' + String(packSize));
  }

  function addProp(name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[' + name + ']';
    input.value = value;
    propsContainer.appendChild(input);
  }

  // ---- Render -------------------------------------------------------------------
  function render() {
    if (!photoList) return;
    photoList.innerHTML = '';

    // Lock the pack picker while uploads are in flight. A pack change mid-upload
    // could assign magnets to photos that were never queued for upload, letting
    // filename fallbacks slip into the order instead of hosted URLs.
    if (packPicker) {
      packPicker.querySelectorAll('input[name="pack"]').forEach(function (r) {
        r.disabled = isUploading;
      });
      packPicker.classList.toggle('is-locked', isUploading);
    }

    photos.forEach(function (photo) {
      var li = document.createElement('li');
      li.className = 'photo-row';

      var thumb = document.createElement('div');
      thumb.className = 'photo-row__thumb';
      var img = document.createElement('img');
      img.src = photo.objectUrl;
      img.alt = 'Preview of ' + photo.file.name;
      // reflect saved crop roughly in the thumbnail via object-position
      thumb.appendChild(img);
      li.appendChild(thumb);

      var meta = document.createElement('div');
      meta.className = 'photo-row__meta';
      var name = document.createElement('p');
      name.className = 'photo-row__name';
      name.textContent = photo.file.name;
      meta.appendChild(name);

      photo.warnings.forEach(function (w) {
        var warn = document.createElement('p');
        warn.className = 'photo-row__warn';
        warn.setAttribute('role', 'alert');
        warn.textContent = '⚠ ' + w;
        meta.appendChild(warn);
      });

      // Per-photo upload progress bar (shown while uploading)
      if (photo.uploadProgress !== null && !photo.uploadError) {
        li.classList.add('photo-row--uploading');
        var progressWrap = document.createElement('div');
        progressWrap.className = 'photo-row__progress';
        var progressFill = document.createElement('div');
        progressFill.className = 'photo-row__progress-fill';
        progressFill.style.width = photo.uploadProgress + '%';
        progressFill.setAttribute('role', 'progressbar');
        progressFill.setAttribute('aria-valuenow', String(photo.uploadProgress));
        progressFill.setAttribute('aria-valuemin', '0');
        progressFill.setAttribute('aria-valuemax', '100');
        progressFill.setAttribute('aria-label', 'Upload progress for ' + photo.file.name);
        progressWrap.appendChild(progressFill);
        meta.appendChild(progressWrap);

        // Cancel button — lets the customer abort a stuck upload instead of
        // waiting indefinitely. Routes through the normal failure/retry path.
        var cancelBtn = smallBtn('Cancel', (function (pid) {
          return function () { cancelUpload(pid); };
        }(photo.id)));
        cancelBtn.className = 'btn btn--outline btn--small photo-row__cancel-btn';
        cancelBtn.setAttribute('aria-label', 'Cancel upload of ' + photo.file.name);
        meta.appendChild(cancelBtn);
      }

      // Per-photo upload error with retry button
      if (photo.uploadError) {
        li.classList.add('photo-row--error');
        var errEl = document.createElement('p');
        errEl.className = 'photo-row__upload-error';
        errEl.setAttribute('role', 'alert');
        errEl.textContent = '✕ Upload failed — ' + photo.uploadError;
        meta.appendChild(errEl);
      }

      var controls = document.createElement('div');
      controls.className = 'photo-row__controls';
      // Hide standard controls while this photo is actively uploading (no error yet)
      if (photo.uploadProgress !== null && !photo.uploadError) {
        controls.hidden = true;
      }

      // qty stepper
      var stepper = document.createElement('div');
      stepper.className = 'qty-stepper';
      var minus = stepBtn('−', 'Fewer magnets of ' + photo.file.name, function () { setQty(photo.id, photo.qty - 1); });
      var qty = document.createElement('input');
      qty.type = 'number';
      qty.min = '0';
      qty.max = String(packSize);
      qty.value = String(photo.qty);
      qty.inputMode = 'numeric';
      qty.setAttribute('aria-label', 'Magnets of ' + photo.file.name);
      qty.addEventListener('change', function () { setQty(photo.id, parseInt(qty.value || '0', 10)); });
      var plus = stepBtn('+', 'More magnets of ' + photo.file.name, function () { setQty(photo.id, photo.qty + 1); });
      stepper.appendChild(minus); stepper.appendChild(qty); stepper.appendChild(plus);
      controls.appendChild(stepper);

      controls.appendChild(smallBtn('Adjust crop', function () { openCrop(photo.id); }));
      controls.appendChild(smallBtn('Replace', function () { replacePhoto(photo.id); }));
      controls.appendChild(smallBtn('Remove', function () { removePhoto(photo.id); }));

      // Retry button — only shown when this photo's upload failed
      if (photo.uploadError) {
        var retryBtn = smallBtn('Retry upload', (function (pid) {
          return function () { retryUpload(pid); };
        }(photo.id)));
        retryBtn.className = 'btn btn--outline btn--small photo-row__retry-btn';
        controls.appendChild(retryBtn);
      }

      meta.appendChild(controls);
      li.appendChild(meta);
      photoList.appendChild(li);
    });

    // "Cancel all uploads" button in the counter area while uploads run
    if (counter && !cancelAllBtn) {
      cancelAllBtn = document.createElement('button');
      cancelAllBtn.type = 'button';
      cancelAllBtn.className = 'btn btn--outline btn--small assign-counter__cancel-all';
      cancelAllBtn.textContent = 'Cancel all uploads';
      cancelAllBtn.addEventListener('click', cancelAllUploads);
      counter.appendChild(cancelAllBtn);
    }
    if (cancelAllBtn) cancelAllBtn.hidden = !isUploading;

    // counter — shows upload progress during upload, assignment otherwise
    var assigned = totalAssigned();
    var hasPhotos = photos.length > 0;
    counter.hidden = !hasPhotos;
    if (hasPhotos) {
      if (isUploading) {
        // Show overall upload progress: "Uploading 2 of 3 photos…"
        counterText.textContent = 'Uploading ' + uploadComplete + ' of ' + uploadTotal + ' photo' + (uploadTotal === 1 ? '' : 's') + '…';
        counterFill.style.width = uploadTotal > 0 ? Math.min(100, (uploadComplete / uploadTotal) * 100) + '%' : '0%';
        counter.classList.remove('is-complete', 'is-over');
        announceStatus('Uploading ' + Math.min(uploadComplete + 1, uploadTotal) + ' of ' + uploadTotal + ' photo' + (uploadTotal === 1 ? '' : 's') + '…');
      } else {
        counterText.textContent = 'Assigned: ' + assigned + ' of ' + packSize + ' magnets';
        counterFill.style.width = Math.min(100, (assigned / packSize) * 100) + '%';
        counter.classList.toggle('is-complete', assigned === packSize);
        counter.classList.toggle('is-over', assigned > packSize);
      }
    }
    assignHint.hidden = !hasPhotos || isUploading || assigned === packSize;
    if (!assignHint.hidden) {
      assignHint.textContent = assigned > packSize
        ? 'You have assigned ' + (assigned - packSize) + ' too many — reduce a photo\'s count.'
        : (packSize - assigned) + ' magnet' + (packSize - assigned === 1 ? '' : 's') + ' still unassigned.';
    }

    useForAllBtn.hidden = !(photos.length >= 1 && packSize > 1);

    // ATC gating
    var ready = !isUploading && hasPhotos && assigned === packSize && photos.every(function (p) {
      return !p.warnings.some(function (w) { return w.indexOf('Unsupported') === 0; });
    });
    setAtcState(ready);
    syncProperties();
  }

  function stepBtn(text, label, onClick) {
    var b = document.createElement('button');
    b.type = 'button';
    b.textContent = text;
    b.setAttribute('aria-label', label);
    b.addEventListener('click', onClick);
    return b;
  }

  function smallBtn(text, onClick) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn--outline btn--small';
    b.textContent = text;
    b.addEventListener('click', onClick);
    return b;
  }

  function setAtcState(ready) {
    var label = isUploading ? uploadingLabel : (ready ? readyLabel : lockedLabel);
    document.querySelectorAll('[data-atc-label]').forEach(function (el) { el.textContent = label; });
    if (atcBtn) atcBtn.disabled = !ready || isUploading;
    if (stickyBtn) stickyBtn.disabled = !ready || isUploading;
    if (atcHelp) atcHelp.hidden = ready || isUploading;
  }

  // ---- Sticky mobile bar -------------------------------------------------------------
  function initSticky() {
    if (!stickyBar) return;
    stickyBar.hidden = false;
    document.body.classList.add('has-sticky-atc');
    if (stickyBtn && form) {
      stickyBtn.addEventListener('click', function () {
        if (form.requestSubmit) form.requestSubmit(); else form.submit();
      });
    }
  }

  // ---- Pickup validation before submit ------------------------------------------------
  function validatePickup() {
    var pickupRadio = document.getElementById('FulfillPickup');
    if (!pickupRadio || !pickupRadio.checked) return true;
    var missing = Array.prototype.filter.call(
      document.querySelectorAll('[data-pickup-required]'),
      function (el) { return !el.value.trim(); }
    );
    if (missing.length) {
      missing[0].focus();
      missing[0].setAttribute('aria-invalid', 'true');
      alert('Please add your pickup name and phone number so we can text you when your order is ready.');
      return false;
    }
    return true;
  }

  // ---- Async submit: upload photos → write URLs → submit form ---------------------
  // This intercepts the cart form submit, uploads every assigned photo to the
  // storage endpoint, writes hosted URLs back into the hidden property inputs,
  // then re-submits the form programmatically.
  function initSubmitGuard() {
    if (!form) return;

    form.addEventListener('submit', function (e) {
      // Always run synchronous pickup validation first.
      if (!validatePickup()) {
        e.preventDefault();
        return;
      }

      // If no upload endpoint is configured, skip async flow entirely.
      if (!UPLOAD_URL) return;

      // Prevent default so we can upload files before the cart add.
      e.preventDefault();

      if (isUploading) return;   // guard against double-submit

      var assignedPhotos = photos.filter(function (p) { return p.qty > 0; });

      // Photos that already have a hosted URL (re-submit after a partial
      // failure) can skip the upload step.
      var pending = assignedPhotos.filter(function (p) { return !p.hostedUrl; });

      if (pending.length === 0) {
        // All photos already uploaded — submit immediately.
        form.submit();
        return;
      }

      isUploading = true;
      uploadTotal = pending.length;
      uploadComplete = 0;
      // Reset any stale error/progress state from a previous attempt
      pending.forEach(function (p) { p.uploadError = null; p.uploadProgress = 0; });
      render();   // shows "Uploading X of Y photos…" label and disables buttons

      var remaining = pending.length;

      function onPhotoSettled() {
        remaining--;
        if (remaining > 0) return;
        // All photos have resolved (success or error)
        var failed = pending.filter(function (p) { return p.uploadError; });
        isUploading = false;
        if (failed.length === 0) {
          announceStatus('All photos uploaded — adding to cart');
          syncProperties();
          form.submit();
        } else {
          // Leave failed rows highlighted; the user can retry each one
          render();
        }
      }

      pending.forEach(function (photo) {
        uploadToStorage(photo, function (pct) {
          photo.uploadProgress = pct;
          render();
        }).then(function (url) {
          if (url) photo.hostedUrl = url;
          photo.uploadProgress = null;  // clear bar on success
          uploadComplete++;
          render();
          onPhotoSettled();
        }).catch(function (err) {
          photo.uploadError = err && err.message ? err.message : 'Upload failed';
          photo.uploadProgress = null;
          announceError('Upload failed for ' + photo.file.name + ' — ' + photo.uploadError + '. Use the Retry upload button to try again.');
          render();
          onPhotoSettled();
        });
      });
    });
  }

  // ---- Per-photo retry ----------------------------------------------------------
  // Called when the customer clicks "Retry upload" on a failed photo row.
  // Re-uploads just that one photo; if it succeeds and all other assigned photos
  // already have hosted URLs, the form is submitted automatically.
  function retryUpload(id) {
    var photo = photos.find(function (p) { return p.id === id; });
    if (!photo || !UPLOAD_URL) return;

    photo.uploadError = null;
    photo.uploadProgress = 0;
    isUploading = true;
    uploadTotal = 1;
    uploadComplete = 0;
    render();

    uploadToStorage(photo, function (pct) {
      photo.uploadProgress = pct;
      render();
    }).then(function (url) {
      if (url) photo.hostedUrl = url;
      photo.uploadProgress = null;
      uploadComplete = 1;
      isUploading = false;
      render();
      // If every assigned photo now has a hosted URL, go ahead and submit
      var allReady = photos.filter(function (p) { return p.qty > 0; })
                          .every(function (p) { return !!p.hostedUrl; });
      if (allReady) {
        announceStatus('All photos uploaded — adding to cart');
        syncProperties();
        form.submit();
      } else {
        announceStatus('Photo ' + photo.file.name + ' uploaded.');
      }
    }).catch(function (err) {
      photo.uploadError = err && err.message ? err.message : 'Upload failed';
      photo.uploadProgress = null;
      isUploading = false;
      announceError('Upload failed for ' + photo.file.name + ' — ' + photo.uploadError + '. Use the Retry upload button to try again.');
      render();
    });
  }

  useForAllBtn.addEventListener('click', useForAll);
  initPackPicker();
  initUploader();
  initCrop();
  initSticky();
  initSubmitGuard();
})();
